import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Download, Plus, AlertCircle, Loader2, CheckCircle, ArrowRight, Brain } from 'lucide-react';
import { AIImageEnhancementService, EnhancementOptions, EnhancementProgress, EnhancementResult } from '../lib/ai-image-enhancement';
import { CreditService } from '../lib/credits';
import { UnifiedCreditService } from '../lib/unified-credit-service';
import { VersionService } from '../lib/versions';
import { supabase } from '../lib/supabase';
import type { AnalysisResult } from '../lib/ai-analysis';

type ModalStep = 'confirmation' | 'enhancing' | 'result' | 'analyzing';

interface CreativeEnhancementModalProps {
  isOpen: boolean;
  onClose: () => void;
  creativeId: string;
  creativeUrl: string;
  creativeName: string;
  analysisData: AnalysisResult;
  analysisId?: string; // Optional analysis ID for saving enhanced image
  onEnhancementComplete?: (result: EnhancementResult) => void;
}

// Funny rotating messages for loading state
const loadingMessages = [
  "Teaching AI about color theory...",
  "Asking pixels to behave better...",
  "Sprinkling some design magic...",
  "Convincing your creative to look sharper...",
  "Negotiating with the contrast slider...",
  "Downloading inspiration from the cloud...",
  "Polishing those rough edges...",
  "Making your competitors jealous...",
  "Adding that je ne sais quoi...",
  "Turning pixels into perfection...",
  "Consulting with the design gods...",
  "Applying some visual vitamins...",
  "Making your creative Instagram-worthy...",
  "Channeling our inner Picasso...",
  "Optimizing for maximum wow factor...",
  "Teaching your image to pose better...",
  "Adding a dash of awesome sauce...",
  "Convincing Meta to love your ad...",
  "Making Google Ads smile...",
  "Preparing for viral status..."
];

export const CreativeEnhancementModal: React.FC<CreativeEnhancementModalProps> = ({
  isOpen,
  onClose,
  creativeId,
  creativeUrl,
  creativeName,
  analysisData,
  analysisId,
  onEnhancementComplete
}) => {
  const [currentStep, setCurrentStep] = useState<ModalStep>('confirmation');
  const [enhancementOptions] = useState<EnhancementOptions>({
    quality: 'high',
    maintainBranding: true,
    customInstructions: ''
  });
  const [progress, setProgress] = useState<EnhancementProgress | null>(null);
  const [enhancementResult, setEnhancementResult] = useState<EnhancementResult | null>(null);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [isSavingVersion, setIsSavingVersion] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [newVersionId, setNewVersionId] = useState<string | null>(null);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisMessage, setAnalysisMessage] = useState('');

  // Reset modal state when closed
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep('confirmation');
      setEnhancementResult(null);
      setError(null);
      setProgress(null);
      setShowSuccessMessage(false);
      setNewVersionId(null);
      setAnalysisProgress(0);
      setAnalysisMessage('');
    }
  }, [isOpen]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      
      // Prevent scrolling
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore scrolling
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Load credit balance when modal opens
  useEffect(() => {
    if (isOpen) {
      loadCreditBalance();
    }
  }, [isOpen]);

  // Rotate loading messages
  useEffect(() => {
    if (currentStep === 'enhancing') {
      const interval = setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [currentStep]);

  const loadCreditBalance = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const creditData = await UnifiedCreditService.getCreditData(user.id);
        setCreditBalance(creditData.availableCredits);
      } else {
        setCreditBalance(0);
      }
    } catch (error) {
      console.error('Failed to load credit balance:', error);
      setCreditBalance(0);
    }
  };

  const generateEnhancementSuggestions = useCallback(() => {
    const suggestions: string[] = [];

    if (analysisData.platforms) {
      Object.values(analysisData.platforms).forEach((platform) => {
        if (platform.issues && platform.issues.length > 0) {
          platform.issues.forEach((issue) => {
            if (issue.severity === 'high' || issue.severity === 'critical') {
              // Extract category from issue title or description
              const issueText = `${issue.title} ${issue.description}`.toLowerCase();
              if (issueText.includes('text') || issueText.includes('font') || issueText.includes('readability')) {
                suggestions.push('Improve text readability and contrast');
              } else if (issueText.includes('brand') || issueText.includes('logo')) {
                suggestions.push('Enhance brand visibility');
              } else if (issueText.includes('composition') || issueText.includes('layout') || issueText.includes('visual')) {
                suggestions.push('Optimize visual composition');
              }
            }
          });
        }
      });
    }

    if (analysisData.overallScore && analysisData.overallScore < 80) {
      suggestions.push('Enhance visual appeal and professional quality');
      suggestions.push('Improve color contrast and visual hierarchy');
    }

    return suggestions.length > 0 ? suggestions : ['Enhanced visual quality and professional appearance'];
  }, [analysisData]);

  const handleEnhance = async () => {
    if (!creditBalance || creditBalance < 1) {
      setError('Insufficient credits. You need 1 credit to enhance a creative.');
      return;
    }

    setCurrentStep('enhancing');
    setError(null);
    setProgress({ stage: 'analyzing', progress: 0, message: 'Starting enhancement...' });

    try {
      // First, deduct credits
      const creditResult = await CreditService.deductCreditsForEnhancement(creativeId);
      
      if (!creditResult.success) {
        setError('Insufficient credits. Please upgrade your plan or purchase more credits.');
        setCurrentStep('confirmation');
        return;
      }

      // Update credit balance
      setCreditBalance(creditResult.newBalance);

      // Perform enhancement with analysis ID if provided
      const result = await AIImageEnhancementService.enhanceCreative(
        creativeId,
        creativeUrl,
        analysisData,
        { ...enhancementOptions, analysisId },
        setProgress
      );

      if (result.success) {
        setEnhancementResult(result);
        
        // Automatically save to analysis if analysisId is provided
        if (analysisId) {
          await saveEnhancedVersionToAnalysis(result);
        }
        
        setCurrentStep('result');
        onEnhancementComplete?.(result);
      } else {
        setError(result.error || 'Enhancement failed');
        setCurrentStep('confirmation');
      }
    } catch (error) {
      console.error('Enhancement error:', error);
      setError('An unexpected error occurred during enhancement');
      setCurrentStep('confirmation');
    }
  };

  const handleDownload = () => {
    if (enhancementResult?.enhancedImageUrl) {
      const link = document.createElement('a');
      link.href = enhancementResult.enhancedImageUrl;
      link.download = `enhanced_${creativeName}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const saveEnhancedVersionToAnalysis = async (result: EnhancementResult) => {
    if (!analysisId || !result.enhancedImageUrl) return;
    
    try {
      // Upload enhanced image to storage
      const { StorageService } = await import('../lib/storage');
      const uploadResult = await StorageService.uploadEnhancedImage(
        result.enhancedImageUrl,
        creativeName,
        analysisId
      );
      
      if (uploadResult.error) {
        throw new Error(uploadResult.error);
      }
      
      if (!uploadResult.url) {
        throw new Error('Failed to get URL from upload result');
      }
      
      // Get the actual creative_id from the analysis record
      const { data: analysisRecord, error: analysisError } = await supabase
        .from('analyses')
        .select('creative_id')
        .eq('id', analysisId)
        .single();
        
      if (analysisError || !analysisRecord?.creative_id) {
        throw new Error('Failed to get creative ID from analysis');
      }
      
      // Store in enhanced_versions table for multiple versions support
      const { error: versionError } = await supabase
        .from('enhanced_versions')
        .insert([{
          analysis_id: analysisId,
          creative_id: analysisRecord.creative_id,
          enhanced_image_url: uploadResult.url,
          version_name: `Enhanced Version ${new Date().toLocaleString()}`,
          enhancement_options: enhancementOptions as any, // Cast to any since EnhancementOptions is compatible with Json
          metadata: {
            timestamp: new Date().toISOString(),
            originalScore: analysisData.overallScore,
            uploadResult: uploadResult
          }
        }]);
        
      if (versionError) {
        throw versionError;
      }
      
      console.log('✅ Enhanced version automatically saved to analysis');
    } catch (error) {
      console.error('Error automatically saving enhanced version:', error);
      // Don't set error state here since it's automatic - just log it
    }
  };

  const handleSaveAsNewVersion = async () => {
    if (!enhancementResult?.enhancedImageUrl) return;

    setIsSavingVersion(true);
    try {
      // First, download the enhanced image and create a File object
      const response = await fetch(enhancementResult.enhancedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], `enhanced_${creativeName}_${Date.now()}.png`, { type: 'image/png' });
      
      // Get the original creative ID (handle analysis ID case)
      let actualCreativeId = creativeId;
      if (creativeId.startsWith('analysis-')) {
        const { data: analysisRecord, error: analysisError } = await supabase
          .from('analyses')
          .select('creative_id')
          .eq('id', creativeId)
          .single();
          
        if (analysisError || !analysisRecord?.creative_id) {
          throw new Error('Failed to find original creative from analysis');
        }
        actualCreativeId = analysisRecord.creative_id;
      }

      // Upload the enhanced image to storage
      const { StorageService } = await import('../lib/storage');
      const uploadResult = await StorageService.uploadCreative(file);

      // Create the version request
      const versionRequest = {
        originalCreativeId: actualCreativeId,
        newFileUrl: uploadResult.url,
        name: `${creativeName} (Enhanced)`,
        versionName: 'AI Enhanced',
        versionNotes: `Enhanced using AI based on analysis recommendations. Original score: ${analysisData.overallScore || 'N/A'}`,
        fileSize: file.size,
        fileType: file.type,
        type: file.type.startsWith('image/') ? 'image' : 'video'
      };

      // Create the new version
      const newVersion = await VersionService.createNewVersion(versionRequest);

      // Store the new version ID and switch to analysis step
      setNewVersionId(newVersion.id);
      setCurrentStep('analyzing');
      setAnalysisProgress(0);
      setAnalysisMessage('Initializing analysis for enhanced version...');
      
      console.log('✅ Enhanced version created successfully:', newVersion.id);
      console.log('🔄 Starting analysis for the new version');
      
      // Now start the analysis for the new version
      const { CreativeService } = await import('../lib/creatives');
      
      const analysisResult = await CreativeService.reanalyzeCreative(
        newVersion.id,
        undefined, // platforms (will analyze all by default)
        undefined, // campaignType
        undefined, // industry
        (progress) => {
          console.log('Analysis progress:', progress);
          setAnalysisProgress(progress.progress);
          setAnalysisMessage(progress.message);
        }
      );

      console.log('✅ Analysis completed successfully for enhanced version');
      
      // Update the version's creative record with the analysis data for version history display
      try {
        console.log('📊 Updating version with analysis data for version history...');
        
        // Get the full analysis data from the analyses table
        const { data: analysisRecord, error: fetchError } = await supabase
          .from('analyses')
          .select('analysis_data')
          .eq('id', analysisResult.analysisId)
          .single();

        if (fetchError) {
          console.warn('⚠️ Failed to fetch analysis data:', fetchError);
        } else if (analysisRecord?.analysis_data) {
          // Update the creative record with the full analysis data
          const { error: updateError } = await supabase
            .from('creatives')
            .update({ 
              analysis_data: analysisRecord.analysis_data,
              status: 'analyzed'
            })
            .eq('id', newVersion.id);

          if (updateError) {
            console.warn('⚠️ Failed to update version with analysis data:', updateError);
          } else {
            console.log('✅ Version updated with analysis data for version history');
          }
        }
      } catch (updateError) {
        console.warn('⚠️ Error updating version with analysis data:', updateError);
      }
      
      // Navigate to the new analysis page (same as AnalysisPage reanalyze flow)
      if (analysisResult.analysisId) {
        console.log('🧭 Redirecting to new analysis:', analysisResult.analysisId);
        if (onEnhancementComplete) {
          onEnhancementComplete({
            success: true,
            enhancedImageUrl: enhancementResult.enhancedImageUrl,
            message: 'Enhanced version created and analyzed successfully!',
            newVersionId: newVersion.id,
            creditDeducted: true
          } as EnhancementResult);
        }
        
        // Navigate to the analysis page using the standard method
        window.location.href = `/dashboard/analysis?id=${analysisResult.analysisId}`;
      }
      
    } catch (error) {
      console.error('Failed to save as new version:', error);
      setError(`Failed to create enhanced version: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSavingVersion(false);
    }
  };

  const enhancementSuggestions = generateEnhancementSuggestions();

  // Render different content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 'confirmation':
        return renderConfirmationStep();
      case 'enhancing':
        return renderEnhancingStep();
      case 'result':
        return renderResultStep();
      case 'analyzing':
        return renderAnalyzingStep();
    }
  };

  const renderConfirmationStep = () => (
    <div className="p-4 sm:p-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
        {/* Left side - Original Image */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Original Creative</h3>
          <div className="relative bg-gray-100 rounded-lg overflow-hidden">
            <img
              src={creativeUrl}
              alt={creativeName}
              className="w-full h-48 sm:h-64 object-contain"
            />
          </div>
        </div>

        {/* Right side - Enhancement Details */}
        <div className="space-y-6">
          {/* Credit Balance */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Available Credits:</span>
              <span className={`text-lg font-semibold ${
                creditBalance !== null && creditBalance >= 1 ? 'text-green-600' : 'text-red-600'
              }`}>
                {creditBalance !== null ? creditBalance : '...'}
              </span>
            </div>
            {creditBalance !== null && creditBalance < 1 && (
              <div className="mt-2 flex items-center space-x-2 text-red-600">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">Insufficient credits for enhancement</span>
              </div>
            )}
          </div>

          {/* AI Suggestions */}
          <div>
            <h4 className="font-medium mb-3 flex items-center space-x-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <span>AI will improve:</span>
            </h4>
            <ul className="space-y-2">
              {enhancementSuggestions.slice(0, 4).map((suggestion, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">{suggestion}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Enhancement Cost */}
          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-purple-900">Enhancement Cost:</span>
              <span className="text-lg font-bold text-purple-600">1 Credit</span>
            </div>
            <p className="text-xs text-purple-700 mt-1">Processing time: ~2 minutes</p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="text-red-800 text-sm">{error}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end mt-6 sm:mt-8 pt-4 border-t border-gray-200">
        <button
          onClick={handleEnhance}
          disabled={!creditBalance || creditBalance < 1}
          className="flex items-center space-x-2 px-4 sm:px-6 py-2 bg-purple-600 text-white text-sm sm:text-base rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Sparkles className="w-4 h-4" />
          <span>Enhance Creative</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  const renderEnhancingStep = () => (
    <div className="p-4 sm:p-8 min-h-[400px] sm:min-h-[500px] flex flex-col items-center justify-center">
      {/* Animated loader */}
      <div className="relative mb-8">
        <div className="w-32 h-32 rounded-full bg-purple-100 flex items-center justify-center">
          <Sparkles className="w-16 h-16 text-purple-600 animate-pulse" />
        </div>
        <div className="absolute inset-0 w-32 h-32 rounded-full border-4 border-purple-600 border-t-transparent animate-spin" />
      </div>

      {/* Progress */}
      <div className="w-full max-w-md mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Progress</span>
          <span className="text-sm text-gray-500">{progress?.progress || 0}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-purple-600 h-2 rounded-full transition-all duration-500"
            style={{ width: `${progress?.progress || 0}%` }}
          />
        </div>
      </div>

      {/* Rotating message */}
      <motion.p
        key={loadingMessageIndex}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="text-lg text-gray-700 text-center mb-2"
      >
        {loadingMessages[loadingMessageIndex]}
      </motion.p>
      
      <p className="text-sm text-gray-500">
        {progress?.message || 'Initializing enhancement...'}
      </p>
    </div>
  );

  const renderResultStep = () => (
    <div className="p-4 sm:p-8">
      {/* Success message */}
      <div className="flex items-center justify-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircle className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Enhancement Complete!</h3>
          <p className="text-sm text-gray-600">Your creative has been successfully enhanced</p>
        </div>
      </div>

      {/* Before/After Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Original</h4>
          <div className="relative bg-gray-100 rounded-lg overflow-hidden">
            <img
              src={creativeUrl}
              alt="Original"
              className="w-full h-48 sm:h-64 object-contain"
            />
          </div>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Enhanced</h4>
          <div className="relative bg-gray-100 rounded-lg overflow-hidden">
            {enhancementResult?.enhancedImageUrl && (
              <img
                src={enhancementResult.enhancedImageUrl}
                alt="Enhanced"
                className="w-full h-48 sm:h-64 object-contain"
              />
            )}
          </div>
        </div>
      </div>

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <h4 className="font-medium text-green-900">Version Created Successfully!</h4>
              <p className="text-sm text-green-700">Analysis is running in the background for your new enhanced version.</p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="space-y-4">
        {/* Primary actions row */}
        {/* Success message */}
        {analysisId && (
          <div className="flex items-center justify-center space-x-2 px-4 py-3 bg-green-100 text-green-800 rounded-lg mb-4">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Automatically saved to analysis</span>
          </div>
        )}
        
        {/* Primary actions row */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleDownload}
            disabled={showSuccessMessage}
            className="flex-1 sm:flex-none flex items-center justify-center space-x-2 px-6 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Download</span>
          </button>
          
          <button
            onClick={handleSaveAsNewVersion}
            disabled={isSavingVersion || showSuccessMessage}
            className="flex-1 sm:flex-none flex items-center justify-center space-x-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSavingVersion ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : showSuccessMessage ? (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>Version Created!</span>
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                <span>Save as New Version</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  const renderAnalyzingStep = () => (
    <div className="p-4 sm:p-8 min-h-[400px] sm:min-h-[500px] flex flex-col items-center justify-center">
      {/* Animated loader */}
      <div className="relative mb-8">
        <div className="w-32 h-32 rounded-full bg-purple-100 flex items-center justify-center">
          <Brain className="w-16 h-16 text-purple-600 animate-pulse" />
        </div>
        <div className="absolute inset-0 w-32 h-32 rounded-full border-4 border-purple-600 border-t-transparent animate-spin" />
      </div>

      {/* Progress */}
      <div className="w-full max-w-md mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Analysis Progress</span>
          <span className="text-sm text-gray-500">{analysisProgress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-purple-600 h-2 rounded-full transition-all duration-500"
            style={{ width: `${analysisProgress}%` }}
          />
        </div>
      </div>

      {/* Status message */}
      <motion.div
        key={analysisMessage}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <p className="text-lg text-gray-700 mb-2">Analyzing Enhanced Version</p>
        <p className="text-sm text-gray-500">
          {analysisMessage || 'Processing your enhanced creative...'}
        </p>
      </motion.div>

      {/* Version info */}
      {newVersionId && (
        <div className="mt-6 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2 text-green-700">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">Version created successfully! Running analysis...</span>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && currentStep !== 'enhancing' && currentStep !== 'analyzing' && onClose()}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                    {currentStep === 'confirmation' && 'Enhance Creative with AI'}
                    {currentStep === 'enhancing' && 'Enhancement in Progress'}
                    {currentStep === 'result' && 'Enhancement Complete'}
                    {currentStep === 'analyzing' && 'Analyzing Enhanced Version'}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {currentStep === 'confirmation' && 'Review and confirm enhancement'}
                    {currentStep === 'enhancing' && 'Please wait while we enhance your creative'}
                    {currentStep === 'result' && 'Your enhanced creative is ready'}
                    {currentStep === 'analyzing' && 'Please wait while we analyze your enhanced version'}
                  </p>
                </div>
              </div>
              {currentStep !== 'enhancing' && currentStep !== 'analyzing' && (
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {renderStepContent()}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};